/**
 * Fase 4 — La cuenta corriente de los clientes.
 *
 * Arriba, quién debe cuánto y DESDE CUÁNDO. Lo segundo importa tanto como lo
 * primero: dos clientes que deben un millón, uno de la semana pasada y otro
 * de hace cuatro meses, no son la misma situación, y el saldo solo no los
 * distingue.
 *
 * Todos los cálculos los hace el servidor. Esta pantalla no suma plata: la
 * muestra y ofrece lo que falta cobrar de cada factura.
 */

import { formatearPesos, formatearFecha } from './formato.js';

const $ = (sel, dentro = document) => dentro.querySelector(sel);

function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

function aCentavos(texto) {
  const limpio = String(texto ?? '').replace(/\./g, '').replace(',', '.').trim();
  if (limpio === '') return '0';
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return null;
  const [entero, decimal = ''] = limpio.split('.');
  return `${entero}${decimal.padEnd(2, '0')}`;
}

const TIPOS_COMPROBANTE = {
  factura_a: 'Factura A', factura_b: 'Factura B', factura_c: 'Factura C',
  nota_credito_a: 'Nota de crédito A', nota_credito_b: 'Nota de crédito B',
  nota_credito_c: 'Nota de crédito C',
  nota_debito_a: 'Nota de débito A', nota_debito_b: 'Nota de débito B',
  nota_debito_c: 'Nota de débito C',
};
const MEDIOS = { transferencia: 'Transferencia', efectivo: 'Efectivo',
                 cheque: 'Cheque', echeq: 'e-Cheq' };
const RETENCIONES = { iva: 'IVA', ganancias: 'Ganancias', iibb: 'Ingresos Brutos', suss: 'SUSS' };

const opciones = (lista, etiqueta) =>
  lista.map((f) => `<option value="${f.id}">${etiqueta(f)}</option>`).join('');

const nombreFactura = (r) =>
  `${TIPOS_COMPROBANTE[r.tipo] ?? r.tipo} ${String(r.punto_venta).padStart(4, '0')}-${
    String(r.numero).padStart(8, '0')}`;

export async function pantallaDeCuentaCorriente(contenedor, { pedir }) {
  contenedor.innerHTML = '';
  const [clientesR, viajesR, configR] = await Promise.all([
    pedir('/api/clientes'), pedir('/api/viajes'), pedir('/api/configuracion'),
  ]);
  const clientes = clientesR.ok ? clientesR.cuerpo.clientes : [];
  const viajes = viajesR.ok ? viajesR.cuerpo.viajes : [];
  // El punto de venta sale de la CONFIGURACIÓN de la empresa, no de un 1
  // escrito acá. Emitir en un punto de venta que no es el suyo rompe la
  // numeración que mira AFIP, y eso no se arregla después.
  const puntoVenta = (configR.ok ? configR.cuerpo.valores ?? {} : {})['punto_venta_arca'] ?? '1';

  if (clientes.length === 0) {
    contenedor.appendChild(alerta('aviso',
      'Antes de facturar hace falta al menos un cliente. Se cargan en «Clientes».'));
    return;
  }

  const marco = document.createElement('div');
  marco.innerHTML = `
    <div class="tarjeta" style="margin-bottom:16px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap">
        <div>
          <h2 style="font-size:16px;margin:0 0 3px">Quién debe, y desde cuándo</h2>
          <p style="color:var(--texto-suave);font-size:12.5px;margin:0">
            El saldo solo no alcanza: una deuda de la semana pasada y una de hace cuatro meses
            no son lo mismo.</p>
        </div>
        <div style="display:flex;gap:10px;align-items:flex-end">
          <button type="button" class="boton" data-accion="nueva-factura">Nueva factura</button>
          <button type="button" class="boton principal" data-accion="nuevo-cobro">Registrar cobro</button>
        </div>
      </div>
      <div data-rol="form" hidden style="margin-top:14px"></div>
      <div data-rol="resumen" style="margin-top:14px"></div>
    </div>
    <div class="tarjeta" data-rol="detalle" hidden></div>`;
  contenedor.appendChild(marco);

  const cajaResumen = $('[data-rol="resumen"]', marco);
  const cajaDetalle = $('[data-rol="detalle"]', marco);
  const cajaForm = $('[data-rol="form"]', marco);

  async function refrescar() {
    const r = await pedir('/api/cuenta-corriente');
    if (!r.ok) {
      cajaResumen.innerHTML = '';
      cajaResumen.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo traer la cuenta.'));
      return;
    }
    dibujarResumen(r.cuerpo);
  }

  function dibujarResumen(datos) {
    const conDeuda = datos.clientes.filter((c) => BigInt(c.saldo_cents) !== 0n);
    if (conDeuda.length === 0) {
      cajaResumen.innerHTML =
        `<p style="color:var(--bien-texto);text-align:center;padding:18px 0;margin:0">
           Ningún cliente debe nada.</p>`;
      return;
    }
    const total = conDeuda.reduce((s, c) => s + BigInt(c.saldo_cents), 0n);

    cajaResumen.innerHTML = `
      <div class="tabla-scroll"><table class="datos">
        <thead><tr>
          <th>Cliente</th>
          ${datos.tramos.map((t) => `<th class="plata">${t.nombre}</th>`).join('')}
          <th class="plata">Saldo</th><th>Debe desde</th>
        </tr></thead>
        <tbody>${conDeuda.map((c) => `
          <tr data-cliente="${c.cliente_id}" style="cursor:pointer">
            <td>${c.cliente_nombre}</td>
            ${datos.tramos.map((t) => {
              const v = BigInt(c.por_tramo[t.clave] ?? '0');
              // Sólo se pinta lo vencido: lo que todavía no vence no es un
              // problema y no tiene por qué llamar la atención.
              const color = v > 0n && t.clave !== 'por_vencer'
                ? (t.clave === 'mas_90' ? 'var(--critico)' : 'var(--grave)') : '';
              return `<td class="plata" ${color ? `style="color:${color}"` : ''}>${
                v === 0n ? '—' : formatearPesos(c.por_tramo[t.clave])}</td>`;
            }).join('')}
            <td class="plata" style="font-weight:600">${formatearPesos(c.saldo_cents)}</td>
            <td style="font-size:12.5px;color:var(--texto-suave)">${
              c.debe_desde
                ? `${formatearFecha(c.debe_desde)} · hace ${c.dias_de_la_mas_vieja} días`
                : 'nada vencido'}</td>
          </tr>`).join('')}</tbody>
        <tfoot><tr>
          <td><b>Total</b></td>
          ${datos.tramos.map((t) => {
            const suma = conDeuda.reduce((s, c) => s + BigInt(c.por_tramo[t.clave] ?? '0'), 0n);
            return `<td class="plata">${suma === 0n ? '—' : formatearPesos(suma.toString())}</td>`;
          }).join('')}
          <td class="plata"><b>${formatearPesos(total.toString())}</b></td><td></td>
        </tr></tfoot>
      </table></div>
      <p style="color:var(--texto-suave);font-size:12.5px;margin:10px 0 0">
        Tocá un cliente para ver factura por factura.</p>`;

    for (const fila of cajaResumen.querySelectorAll('[data-cliente]')) {
      fila.addEventListener('click', () => void verDetalle(fila.dataset.cliente));
    }
  }

  async function verDetalle(clienteId) {
    const r = await pedir(`/api/cuenta-corriente/${clienteId}`);
    if (!r.ok) return;
    const nombre = clientes.find((c) => c.id === clienteId)?.razon_social ?? '';
    cajaDetalle.hidden = false;
    cajaDetalle.innerHTML = `
      <h2 style="font-size:16px;margin:0 0 12px">${nombre} — factura por factura</h2>
      ${r.cuerpo.renglones.length === 0
        ? '<p style="color:var(--bien-texto);margin:0">No debe nada.</p>'
        : `<div class="tabla-scroll"><table class="datos">
            <thead><tr>
              <th>Comprobante</th><th>Emitida</th><th>Vence</th>
              <th class="plata">Total</th><th class="plata">Pagado</th>
              <th class="plata">Falta</th><th>Situación</th>
            </tr></thead>
            <tbody>${r.cuerpo.renglones.map((f) => `
              <tr>
                <td>${nombreFactura(f)}</td>
                <td>${formatearFecha(f.fecha_emision)}</td>
                <td>${formatearFecha(f.fecha_vencimiento)}</td>
                <td class="plata">${formatearPesos(f.total_cents)}</td>
                <td class="plata">${formatearPesos(f.imputado_cents)}</td>
                <td class="plata" style="font-weight:600">${formatearPesos(f.pendiente_cents)}</td>
                <td style="font-size:12.5px;color:${
                  f.dias_vencido > 90 ? 'var(--critico)'
                  : f.dias_vencido > 0 ? 'var(--grave)' : 'var(--texto-suave)'}">${
                  f.dias_vencido > 0 ? `vencida hace ${f.dias_vencido} días` : 'todavía no vence'}</td>
              </tr>`).join('')}</tbody>
          </table></div>`}`;
    cajaDetalle.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // --- Nueva factura -----------------------------------------------------
  function formularioDeFactura() {
    cajaForm.hidden = false;
    cajaForm.innerHTML = `
      <div data-rol="errores"></div>
      <form method="post" action="/api/comprobantes" novalidate
            style="border:1px solid var(--borde);border-radius:8px;padding:14px">
        <h3 style="font-size:14px;margin:0 0 12px">Nueva factura</h3>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:0 16px">
          <div class="campo"><label for="fa-tipo">Tipo *</label>
            <select id="fa-tipo" name="tipo">
              ${Object.entries(TIPOS_COMPROBANTE).map(([v, t]) =>
                `<option value="${v}" ${v === 'factura_a' ? 'selected' : ''}>${t}</option>`).join('')}
            </select></div>
          <div class="campo"><label for="fa-pv">Punto de venta *</label>
            <input type="text" id="fa-pv" name="punto_venta" inputmode="numeric"
                   value="${puntoVenta}">
            <div class="ayuda">Sale de Configuración. Cambialo ahí, no acá.</div></div>
          <div class="campo"><label for="fa-num">Número *</label>
            <input type="text" id="fa-num" name="numero" inputmode="numeric">
            <div class="ayuda" data-rol="aviso-numero"></div></div>
          <div class="campo"><label for="fa-cliente">Cliente *</label>
            <select id="fa-cliente" name="cliente_id"><option value="">— elegir —</option>
              ${opciones(clientes, (c) => c.razon_social)}</select></div>
          <div class="campo"><label for="fa-emision">Fecha de emisión *</label>
            <input type="date" id="fa-emision" name="fecha_emision"></div>
          <div class="campo"><label for="fa-vence">Vencimiento</label>
            <input type="date" id="fa-vence" name="fecha_vencimiento">
            <div class="ayuda">Si se deja vacío, vence el día que se emite.</div></div>
          <div class="campo"><label for="fa-viaje">Viaje que se factura</label>
            <select id="fa-viaje" name="viaje_id"><option value="">— ninguno —</option>
              ${opciones(viajes, (v) => `${v.numero} · ${v.origen} → ${v.destino}`)}</select>
            <div class="ayuda">Al elegirlo se completa el renglón con su flete.</div></div>
          <div class="campo"><label for="fa-desc">Qué se cobra *</label>
            <input type="text" id="fa-desc" name="descripcion" value="Servicio de flete"></div>
          <div class="campo"><label for="fa-neto">Neto *</label>
            <input type="text" id="fa-neto" name="neto" inputmode="decimal"></div>
          <div class="campo"><label for="fa-iva">IVA</label>
            <input type="text" id="fa-iva" name="iva" inputmode="decimal"></div>
        </div>
        <p style="margin:8px 0 0;font-size:13px">Total: <b data-rol="total-factura">$ 0,00</b>
          <span style="color:var(--texto-suave)">Lo calcula el sistema, sumando los renglones.</span></p>
        <div style="display:flex;gap:10px;margin-top:12px">
          <button type="submit" class="boton principal">Emitir factura</button>
          <button type="button" class="boton" data-accion="cancelar">Cancelar</button>
        </div>
      </form>`;

    const form = $('form', cajaForm);
    const total = $('[data-rol="total-factura"]', form);
    const avisoNumero = $('[data-rol="aviso-numero"]', form);

    const recalcular = () => {
      const n = aCentavos(form.elements['neto'].value);
      const i = aCentavos(form.elements['iva'].value);
      total.textContent = formatearPesos(((n === null ? 0n : BigInt(n))
                                        + (i === null ? 0n : BigInt(i))).toString());
    };

    // Al elegir un viaje, el renglón se completa con su flete y su IVA.
    form.elements['viaje_id'].addEventListener('change', (e) => {
      const v = viajes.find((x) => x.id === e.target.value);
      if (!v) return;
      form.elements['descripcion'].value = `Flete ${v.origen} → ${v.destino} (${v.numero})`;
      form.elements['neto'].value = (Number(BigInt(v.flete_neto_cents) / 100n)).toString();
      const iva = BigInt(v.flete_neto_cents) * BigInt(Math.round(Number(v.iva_porcentaje) * 100))
                  / 10000n;
      form.elements['iva'].value = (Number(iva / 100n)).toString();
      recalcular();
    });

    // El próximo número libre, sugerido.
    const sugerirNumero = async () => {
      const r = await pedir(`/api/comprobantes/proximo-numero?tipo=${
        form.elements['tipo'].value}&punto_venta=${form.elements['punto_venta'].value}`);
      if (r.ok) {
        form.elements['numero'].value = r.cuerpo.proximo;
        avisoNumero.textContent = `El último cargado fue el ${r.cuerpo.ultimo}. Se puede cambiar.`;
      }
    };
    form.elements['tipo'].addEventListener('change', () => void sugerirNumero());
    form.elements['punto_venta'].addEventListener('change', () => void sugerirNumero());
    void sugerirNumero();

    form.addEventListener('input', recalcular);
    $('[data-accion="cancelar"]', form).addEventListener('click', cerrarForm);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errores = $('[data-rol="errores"]', cajaForm);
      errores.innerHTML = '';
      const r = await pedir('/api/comprobantes', { method: 'POST', body: JSON.stringify({
        tipo: form.elements['tipo'].value,
        punto_venta: Number(form.elements['punto_venta'].value),
        numero: form.elements['numero'].value,
        cliente_id: form.elements['cliente_id'].value,
        fecha_emision: form.elements['fecha_emision'].value,
        fecha_vencimiento: form.elements['fecha_vencimiento'].value,
        estado: 'emitida',
        items: [{
          descripcion: form.elements['descripcion'].value,
          viaje_id: form.elements['viaje_id'].value,
          neto_cents: aCentavos(form.elements['neto'].value) ?? '',
          iva_cents: aCentavos(form.elements['iva'].value) ?? '0',
        }],
      }) });
      if (!r.ok) { mostrarErrores(errores, r); return; }
      cerrarForm();
      await refrescar();
    });
  }

  // --- Nuevo cobro -------------------------------------------------------
  function formularioDeCobro() {
    cajaForm.hidden = false;
    cajaForm.innerHTML = `
      <div data-rol="errores"></div>
      <form method="post" action="/api/cobros" novalidate
            style="border:1px solid var(--borde);border-radius:8px;padding:14px">
        <h3 style="font-size:14px;margin:0 0 12px">Registrar un cobro</h3>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:0 16px">
          <div class="campo"><label for="co-cliente">Cliente *</label>
            <select id="co-cliente" name="cliente_id"><option value="">— elegir —</option>
              ${opciones(clientes, (c) => c.razon_social)}</select></div>
          <div class="campo"><label for="co-fecha">Fecha *</label>
            <input type="date" id="co-fecha" name="fecha"></div>
          <div class="campo"><label for="co-importe">Lo que entró *</label>
            <input type="text" id="co-importe" name="importe" inputmode="decimal">
            <div class="ayuda">La plata que efectivamente entró, ya sin las retenciones.</div></div>
          <div class="campo"><label for="co-medio">Medio *</label>
            <select id="co-medio" name="medio">
              ${Object.entries(MEDIOS).map(([v, t]) =>
                `<option value="${v}" ${v === 'transferencia' ? 'selected' : ''}>${t}</option>`).join('')}
            </select></div>
          <div class="campo"><label for="co-ref">Referencia</label>
            <input type="text" id="co-ref" name="referencia"></div>
        </div>

        <div data-rol="cheque" hidden style="border-top:1px solid var(--borde);margin-top:12px;padding-top:12px">
          <h4 style="font-size:13px;margin:0 0 8px">El cheque</h4>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:0 16px">
            <div class="campo"><label for="co-ch-num">Número *</label>
              <input type="text" id="co-ch-num" name="cheque_numero"></div>
            <div class="campo"><label for="co-ch-banco">Banco</label>
              <input type="text" id="co-ch-banco" name="cheque_banco"></div>
            <div class="campo"><label for="co-ch-cobro">Fecha de cobro *</label>
              <input type="date" id="co-ch-cobro" name="cheque_fecha_cobro">
              <div class="ayuda">Si es diferido, ésta es la fecha que importa.</div></div>
          </div>
        </div>

        <div style="border-top:1px solid var(--borde);margin-top:12px;padding-top:12px">
          <h4 style="font-size:13px;margin:0 0 4px">¿Retuvo algo?</h4>
          <p style="color:var(--texto-suave);font-size:12.5px;margin:0 0 8px">
            Si el cliente retuvo, transfiere menos pero la factura se salda igual: esa plata la
            pagó a la AFIP en nombre nuestro. Cargala acá o la cuenta no va a cerrar.</p>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:0 16px">
            ${Object.entries(RETENCIONES).map(([v, t]) => `
              <div class="campo"><label for="co-ret-${v}">${t}</label>
                <input type="text" id="co-ret-${v}" data-retencion="${v}" inputmode="decimal"></div>`).join('')}
          </div>
        </div>

        <div style="border-top:1px solid var(--borde);margin-top:12px;padding-top:12px">
          <h4 style="font-size:13px;margin:0 0 8px">A qué facturas se imputa</h4>
          <div data-rol="facturas">
            <p style="color:var(--texto-suave);font-size:13px;margin:0">Elegí el cliente.</p>
          </div>
          <p style="margin:10px 0 0;font-size:13px" data-rol="cuadre"></p>
        </div>

        <div style="display:flex;gap:10px;margin-top:12px">
          <button type="submit" class="boton principal">Guardar cobro</button>
          <button type="button" class="boton" data-accion="cancelar">Cancelar</button>
        </div>
      </form>`;

    const form = $('form', cajaForm);
    const cajaFacturas = $('[data-rol="facturas"]', form);
    const cajaCuadre = $('[data-rol="cuadre"]', form);
    const cajaCheque = $('[data-rol="cheque"]', form);
    let pendientes = [];

    const leerRetenciones = () => [...form.querySelectorAll('[data-retencion]')]
      .map((i) => ({ tipo: i.dataset.retencion, importe_cents: aCentavos(i.value) ?? '0' }))
      .filter((r) => r.importe_cents !== '0');

    const leerImputaciones = () => [...form.querySelectorAll('[data-imputar]')]
      .map((i) => ({ comprobante_id: i.dataset.imputar, importe_cents: aCentavos(i.value) ?? '0' }))
      .filter((a) => a.importe_cents !== '0');

    function mostrarCuadre() {
      const entro = BigInt(aCentavos(form.elements['importe'].value) ?? '0');
      const retenido = leerRetenciones().reduce((s, r) => s + BigInt(r.importe_cents), 0n);
      const imputado = leerImputaciones().reduce((s, a) => s + BigInt(a.importe_cents), 0n);
      const diferencia = entro + retenido - imputado;
      cajaCuadre.innerHTML = diferencia === 0n && imputado > 0n
        ? `<span style="color:var(--bien-texto)">✓ La cuenta cierra: entraron ${
            formatearPesos(entro.toString())} + ${formatearPesos(retenido.toString())} retenidos = ${
            formatearPesos(imputado.toString())} imputados.</span>`
        : `<span style="color:var(--grave)">Falta cuadrar: entró ${
            formatearPesos(entro.toString())} + ${formatearPesos(retenido.toString())} retenido = ${
            formatearPesos((entro + retenido).toString())}, y se está imputando ${
            formatearPesos(imputado.toString())}.</span>`;
    }

    async function traerPendientes() {
      const cliente = form.elements['cliente_id'].value;
      if (!cliente) return;
      const importe = aCentavos(form.elements['importe'].value) ?? '0';
      const retenido = leerRetenciones().reduce((s, r) => s + BigInt(r.importe_cents), 0n);
      const aImputar = (BigInt(importe) + retenido).toString();
      const r = await pedir(`/api/clientes/${cliente}/pendientes?importe=${aImputar}`);
      if (!r.ok) return;
      pendientes = r.cuerpo.facturas;
      const sugerido = new Map(r.cuerpo.sugerido.map((s) => [s.comprobante_id, s.importe_cents]));

      cajaFacturas.innerHTML = pendientes.length === 0
        ? '<p style="color:var(--texto-suave);font-size:13px;margin:0">Ese cliente no debe nada.</p>'
        : `<div class="tabla-scroll"><table class="datos">
            <thead><tr><th>Comprobante</th><th>Vence</th><th class="plata">Falta</th>
                       <th class="plata">Se imputa</th></tr></thead>
            <tbody>${pendientes.map((f) => `
              <tr>
                <td>${nombreFactura(f)}</td>
                <td>${formatearFecha(f.fecha_vencimiento)}${f.dias_vencido > 0
                  ? `<br><small style="color:var(--grave)">vencida hace ${f.dias_vencido} días</small>` : ''}</td>
                <td class="plata">${formatearPesos(f.pendiente_cents)}</td>
                <td class="plata"><input type="text" data-imputar="${f.comprobante_id}"
                     inputmode="decimal" style="width:130px;text-align:right"
                     value="${sugerido.has(f.comprobante_id)
                       ? (Number(BigInt(sugerido.get(f.comprobante_id)) / 100n)).toString() : ''}"></td>
              </tr>`).join('')}</tbody>
          </table></div>
          <p style="color:var(--texto-suave);font-size:12.5px;margin:8px 0 0">
            Viene sugerido de la más vieja a la más nueva. Se puede cambiar.</p>`;
      mostrarCuadre();
    }

    form.elements['cliente_id'].addEventListener('change', () => void traerPendientes());
    form.elements['medio'].addEventListener('change', () => {
      const esCheque = ['cheque', 'echeq'].includes(form.elements['medio'].value);
      cajaCheque.hidden = !esCheque;
    });
    form.addEventListener('input', (e) => {
      if (e.target.name === 'importe' || e.target.dataset.retencion) void traerPendientes();
      mostrarCuadre();
    });

    $('[data-accion="cancelar"]', form).addEventListener('click', cerrarForm);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errores = $('[data-rol="errores"]', cajaForm);
      errores.innerHTML = '';
      const medio = form.elements['medio'].value;
      const cuerpo = {
        cliente_id: form.elements['cliente_id'].value,
        fecha: form.elements['fecha'].value,
        importe_cents: aCentavos(form.elements['importe'].value) ?? '',
        medio,
        referencia: form.elements['referencia'].value,
        retenciones: leerRetenciones().map((r) => ({ ...r, fecha: form.elements['fecha'].value })),
        aplicaciones: leerImputaciones(),
      };
      if (['cheque', 'echeq'].includes(medio)) {
        cuerpo.cheque = {
          numero: form.elements['cheque_numero'].value,
          banco: form.elements['cheque_banco'].value,
          fecha_cobro: form.elements['cheque_fecha_cobro'].value,
        };
      }
      const r = await pedir('/api/cobros', { method: 'POST', body: JSON.stringify(cuerpo) });
      if (!r.ok) { mostrarErrores(errores, r); return; }
      cerrarForm();
      await refrescar();
    });
  }

  function mostrarErrores(caja, r) {
    const lista = r.cuerpo?.errores
      ?? [{ rotulo: '', problema: r.cuerpo?.error ?? 'No se pudo guardar.' }];
    for (const e of lista) {
      caja.appendChild(alerta('critico', e.rotulo ? `${e.rotulo}: ${e.problema}` : e.problema));
    }
    caja.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function cerrarForm() { cajaForm.hidden = true; cajaForm.innerHTML = ''; }

  $('[data-accion="nueva-factura"]', marco).addEventListener('click', formularioDeFactura);
  $('[data-accion="nuevo-cobro"]', marco).addEventListener('click', formularioDeCobro);

  await refrescar();
}
